import { getAccessibleNodeColor } from "../nodeColorPalette";
import { getDiagramCanvasBackground } from "../styles";

// Deriva o lineStyle efetivo ("solida" | "tracejada" | "pontilhada") de uma edge: usa o campo
// novo se presente, senão migra a antiga flag booleana `backup` — mantém diagramas/templates
// salvos antes da troca (link de backup vira "tracejada") com a mesma aparência, sem precisar
// de migração de banco (dados são Json livre, ver CLAUDE.md do backend)
export const resolveLineStyle = (data) => {
  if (data?.lineStyle) return data.lineStyle;
  return data?.backup ? "tracejada" : "solida";
};

// Estilo visual (cor + traço) de uma edge a partir do lineStyle e, opcionalmente, de data.cor —
// usado pelo canvas de edição e pelos dois previews read-only (DiagramaEquipamentosPreview/
// TemplatePreview), sempre recalculado com o tema atual, nunca persistido (mesmo raciocínio de
// nodeColorPalette.js: cor derivada do tema não pode ser salva como hex fixo, senão sobrevive à
// troca de tema/paleta do tenant e pode ficar ilegível — foi assim que o link de backup ficou com
// a cor do fundo no tema escuro, usando theme.palette.secondary.main, uma cor sem garantia de
// contraste pro canvas do diagrama. Sem `cor`, toda linha usa theme.palette.primary.main (já
// comprovadamente visível nos dois temas — era a cor do link "normal", nunca teve essa
// reclamação); com `cor`, reusa getAccessibleNodeColor (mesma paleta/lógica de contraste dos
// nós) contra o fundo do canvas — não o do card (background.paper) que os nós usam como
// referência, já que a linha é desenhada direto sobre o canvas
export const getEdgeVisualStyle = (lineStyle, theme, cor) => {
  const corAcessivel = cor
    ? getAccessibleNodeColor(cor, theme, getDiagramCanvasBackground(theme))
    : null;
  const base = {
    stroke: corAcessivel?.border || theme?.palette?.primary?.main,
    strokeWidth: 2,
  };
  if (lineStyle === "tracejada") return { ...base, strokeDasharray: "6 3" };
  if (lineStyle === "pontilhada") {
    return { ...base, strokeDasharray: "1 4", strokeLinecap: "round" };
  }
  return base;
};

// Sanitiza o diagrama para virar template: whitelist estrita de campos.
// Sem ip (dado do circuito específico), sem anexos, sem _equipmentTypes,
// sem callbacks, sem measured/selected — garantindo shape estável no banco
export const sanitizeDiagramForTemplate = (nodes = [], edges = []) => ({
  nodes: (nodes || []).map((node) => ({
    id: node?.id,
    type: node?.type || "equipamento",
    position: { x: node?.position?.x || 0, y: node?.position?.y || 0 },
    data: {
      tipo: node?.data?.tipo,
      nome: node?.data?.nome,
      observacoes: node?.data?.observacoes || "",
      cor: node?.data?.cor || null,
    },
  })),
  edges: (edges || []).map((edge) => ({
    id: edge?.id,
    type: edge?.type || "equipamento",
    source: edge?.source,
    target: edge?.target,
    sourceHandle: edge?.sourceHandle || null,
    targetHandle: edge?.targetHandle || null,
    label: edge?.label || "",
    data: {
      lineStyle: resolveLineStyle(edge?.data),
      controlPoints: edge?.data?.controlPoints || [],
      lineMode: edge?.data?.lineMode || "curva",
      cor: edge?.data?.cor || null,
    },
  })),
});

// Re-deriva o estilo visual das edges a partir do lineStyle/cor (com fallback pro `backup`
// legado) com o tema atual — templates não persistem style
export const buildEdgesComStyle = (edges = [], theme) =>
  (edges || []).map((edge) => ({
    ...edge,
    type: edge?.type || "equipamento",
    style: getEdgeVisualStyle(resolveLineStyle(edge?.data), theme, edge?.data?.cor),
  }));
